<svelte:options runes={false} />
<script>
  // A right-side slide-in overlay panel (#2): the New Strategy form slides in from the left edge of the panel
  // toward the right as an overlay, over a dimmed backdrop. Esc closes, a backdrop click closes, focus is
  // trapped, and the trigger's focus is restored on close. The slide animation is a CSS transform transition.
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';

  const dispatch = createEventDispatcher();

  export let open = false;
  export let title = '';

  let panelEl = null;
  let prevFocus = null;

  function close() { dispatch('close'); }

  function onKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab' || !panelEl) return;
    const focusable = panelEl.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  // When the panel opens, capture the previously-focused element + focus the panel; restore on close.
  $: if (open) {
    queueMicrotask(() => {
      if (!prevFocus) prevFocus = document.activeElement;
      panelEl?.querySelector('input, button, select, textarea')?.focus();
    });
  } else if (prevFocus) {
    const el = prevFocus;
    prevFocus = null;
    queueMicrotask(() => { if (typeof el.focus === 'function') el.focus(); });
  }

  onMount(() => { document.addEventListener('keydown', onKeydown); });
  onDestroy(() => { document.removeEventListener('keydown', onKeydown); });
</script>

<div class="at-slidein" class:open aria-hidden={!open}>
  <div class="at-slidein-backdrop" on:click={close}></div>
  <aside class="at-slidein-panel" bind:this={panelEl} role="dialog" aria-modal="true"
    aria-label={title || 'Panel'} tabindex="-1">
    <header class="at-slidein-header">
      <h2 class="at-slidein-title">{title}</h2>
      <button type="button" class="btn btn-ghost at-slidein-close" on:click={close} aria-label="Close">✕</button>
    </header>
    <div class="at-slidein-body">
      <slot />
    </div>
  </aside>
</div>

<style>
  .at-slidein {
    position: fixed;
    inset: 0;
    z-index: 1000;
    pointer-events: none;
  }
  .at-slidein.open { pointer-events: auto; }
  .at-slidein-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity 0.25s ease;
  }
  .at-slidein.open .at-slidein-backdrop { opacity: 1; }
  .at-slidein-panel {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: min(560px, 92vw);
    background: var(--surface-0, #0f1014);
    border-right: 1px solid var(--border-subtle, #1e1f22);
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.4);
    transform: translateX(-100%);
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .at-slidein.open .at-slidein-panel { transform: translateX(0); }
  .at-slidein-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--border-subtle, #1e1f22);
    flex-shrink: 0;
  }
  .at-slidein-title { margin: 0; font-size: 1.1rem; font-weight: 700; color: var(--text-primary, #e8e9ea); }
  .at-slidein-close { font-size: 1rem; line-height: 1; }
  .at-slidein-body { padding: 1.25rem; overflow-y: auto; flex: 1; }
</style>
